<template>
  <div :class="[sideBySide ? 'col-12 col-md-6' : 'col-12', 'shortlist-column']" v-if="shortlist">
    <div class="row" v-if="hasQuotes">
      <div class="col-8">
        <div class="jump-to">
          <div class="select large ms-0 mb-3">
            <select id="jump-to" name="jump-to" v-model="currentQuoteGroup">
              <template v-for="shortlistOption in options">
                <optgroup v-if="shortlistOption" :label="shortlistOption.alternateName">
                  <template v-if="shortlistOption">
                    <option v-for="quoteGroup, index in shortlistOption.quotes" :value="index">
                      {{ quoteGroup[0].manufacturerName }} {{ quoteGroup[0].modelName }}
                    </option>
                  </template>
                </optgroup>
              </template>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="row scroll-col">
      <div class="col-12">
        <comparison-grid
          v-if="hasQuotes"
          :panel="panel"
          :adminPortalData="adminPortalData"
          :selected-quote="selectedQuote"
          :sideBySide="sideBySide"
          :classes="classes"
          :session="session"
          :shortlist="shortlist"
          :viewPreviousQuoteGroup="viewPreviousQuoteGroup"
          :currentVisibleQuote="currentVisibleQuote"
          :quoteExpired="quoteExpired"
          :viewNextQuoteGroup="viewNextQuoteGroup"
          :currentQuoteGroup="currentQuoteGroup"
          :costsForSalarySacrificeQuote="costsForSalarySacrificeQuote"
          :averagePencePerMile="averagePencePerMile"
          :totalEstimatedElectricCostPerMonth="totalEstimatedElectricCostPerMonth"
          :activeQuoteInGroup="activeQuoteInGroup"
          :saveConfigurationInline="saveConfigurationInline"
          :hasInlineConfiguration="hasInlineConfiguration"
          :driverGradesById="driverGradesById"
          v-on:select-quote="updateSelectedQuote($event)"
        ></comparison-grid>
        <div v-else>
          <p class="text-center" style="font-size:24px;font-style:italic;">There are no quotes to display. Return to <RouterLink :to="{ name: 'Shortlist', params: { scheme: route.params.scheme }}">the vehicle search page</RouterLink>.</p>
        </div>
      </div>
    </div>
    <charging-profile :adminPortalData="adminPortalData" :slideoutVisibleOverride="chargingSlideoutVisible" />
  </div>
  <div class="col-12" v-else>
    <p class="text-center" style="font-size:22px;font-style: italic;">There are no valid vehicles in your shortlist. Return to <RouterLink :to="{ name: 'Shortlist', params: { scheme: route.params.scheme }}">the vehicle search page</RouterLink>.</p>
  </div>
</template>

<script>

import {EventBus} from '@/event-bus.js';
import VehicleImage from "../vehicle-details/vehicle-image";
import ComparisonGrid from "./comparison-grid";
import Vehicle from "../../helpers/vehicle";
import VehicleCosts from "../../helpers/vehicle-costs";
import ChargingProfile from "@/components/charging-profile-component.vue";
import {useRoute} from "vue-router";

export default {

  props: [
    'adminPortalData',
    'panel',
    'showRemoveButton',
    'sideBySide',
    'classes',
    'selectedQuote',
    'options',
    'view',
    'shortlist',
    'jumpTo',
    'session',
    'adminPortalData',
    'annualMileage',
    'saveConfigurationInline',
    'hasInlineConfiguration',
    'homeChargingStandardPercentage',
    'publicChargingStandardPercentage',
    'publicChargingFastPercentage',
    'publicChargingFreePercentage',
    'driverGradesById',
  ],

  components: {
    ChargingProfile,
    ComparisonGrid,
    VehicleImage,
    EventBus,
    Vehicle
  },

  setup() {
    const route = useRoute();
    return { route }
  },

  created() {
    this.selectedShortlist = this.shortlist.shortListID;
  },

  mounted() {
    this.sendQuoteValuesForChargingProfileCalculations();

    EventBus.$on('view-quote-in-group', (panel, index) => {
      if (this.panel == panel) {
        this.activeQuoteInGroup = index;
        this.sendQuoteValuesForChargingProfileCalculations();
      }
    });

    EventBus.$on('slideout-visible', (status) => {
      this.slideoutVisible = status === this.shortlist.quotes[this.currentQuoteGroup][this.activeQuoteInGroup].quoteID;
    });

    EventBus.$on('charging-slideout-visible', (status) => {
      this.chargingSlideoutVisible = true;
    });

    EventBus.$on('charging-slideout-hidden', (status) => {
      this.chargingSlideoutVisible = false;
    });
  },

  data: () => {
    return {
      selectedShortlist: null,

      rangeConfig: {
        step: 0.1,
        range: {
          'min': 1,
          'max': 200
        }
      },

      mpgCalculation: [66],
      defaultMpg: 30,

      currentQuoteGroup: 0,
      activeQuoteInGroup: 0,
      take: 3,

      tooltip: true,
      costsForSalarySacrificeQuote: null,

      chargingSlideoutVisible: false,
    }
  },

  methods: {
    hideSlideout() {
      this.slideoutVisible = false;
    },

    saveChargingProfile() {
      const total = parseInt(this.homeChargingStandardPercentage) + parseInt(this.publicChargingStandardPercentage) + parseInt(this.publicChargingFastPercentage) + parseInt(this.publicChargingFreePercentage);

      if (total !== 100) {
        this.$notify({
          group: 'notify',
          type: 'error',
          title: "Error",
          text: "Please ensure all percentages add up to 100% before saving (current total is " + total + "%)",
        });
        return;
      }

      if (parseInt(this.homeChargingStandardPercentage) < 0 || parseInt(this.publicChargingStandardPercentage) < 0 || parseInt(this.publicChargingFastPercentage) < 0 || parseInt(this.publicChargingFreePercentage) < 0) {
        this.$notify({
          group: 'notify',
          type: 'error',
          title: "Error",
          text: "Percentages cannot be negative",
        });
        return;
      }

      EventBus.$emit('loadingTitle', 'Saving Charging Profile');
      EventBus.$emit('loading', true);

      const newChargingProfile = {
        homeChargingStandardPercentage: this.homeChargingStandardPercentage,
        publicChargingStandardPercentage: this.publicChargingStandardPercentage,
        publicChargingFastPercentage: this.publicChargingFastPercentage,
        publicChargingFreePercentage: this.publicChargingFreePercentage,
      }

      this.$http.post('/api/save-charging-profile', newChargingProfile)
        .then(response => {
          EventBus.$emit('loading', false);
          EventBus.$emit('charging-profile-updated', newChargingProfile);

          this.$notify({
            group: 'notify',
            type: 'success',
            title: 'Success',
            text: 'Charging Profile saved successfully'
          });

          this.hideSlideout();
        })
        .catch(err => {
          EventBus.$emit('loading', false);
          let response = err.response;

          this.$notify({
            group: 'notify',
            type: 'error',
            title: "Error",
            text: response.data.error.message
          });

        });
    },

    sendQuoteValuesForChargingProfileCalculations() {
      if (this.currentVisibleQuote) {
        EventBus.$emit('quote-values-required-for-charging-profile', {
          annualMileage: this.currentVisibleQuote.quote.definition.plan.annualDistance,
          wLTPRangeMiles: this.wLTPRangeMiles,
          batterySizeKWh: this.batterySizeKWh,
        });
      }
    },

    updateSelectedQuote(event) {
      this.$emit('selectedQuote', event);
    },

    shortlistItemConfigured(item) {
      // If paintwork or trim aren't set, the vehicle isn't configured yet.
      return !(item.quote.factoryOptionsGrouped['Paintwork'] === undefined ||
        item.quote.factoryOptionsGrouped['Trim'] === undefined);
    },

    changeShortList(shortListId) {
      this.currentQuoteGroup = 0;
      this.$emit('changeShortList', shortListId);
    },

    resetMPG() {
      this.shortlist.quotes[this.currentQuoteGroup] = this.calculateMpg(this.currentQuote, this.defaultMpg);
      return this.defaultMpg;
    },

    calculateMpg(quote, value) {
      EventBus.$emit('loadingTitle', 'Calculating MPG');
      EventBus.$emit('loading', true);
      this.$http.post('/api/quotation', {
        method: 'GET',
        endpoint: `quotes/${quote.quoteID}/taxCalculations/Latest`,
        uri_params: {
          mpgOverride: value,
          forceRecalculate: 'true'
        }
      })
        .then(response => {
          EventBus.$emit('loading', false);
          quote.taxCalculations = response.data.data;
        });
      return quote.taxCalculations;
    },

    viewPreviousQuoteGroup() {
      this.currentQuoteGroup--;

      if (this.currentQuoteGroup < 0) {
        this.currentQuoteGroup = this.shortlist.quotes.length - 1
      }

      this.activeQuoteInGroup = 0;

      this.sendQuoteValuesForChargingProfileCalculations();

      this.$emit('viewPreviousQuoteGroup', this.currentQuoteGroup)
    },

    viewNextQuoteGroup() {
      this.currentQuoteGroup++;

      if (this.currentQuoteGroup > this.shortlist.quotes.length - 1) {
        this.currentQuoteGroup = 0
      }

      this.activeQuoteInGroup = 0;

      this.sendQuoteValuesForChargingProfileCalculations();

      this.$emit('viewNextQuoteGroup', this.currentQuoteGroup)
    },

    getBikValue(shortlistItem) {
      return shortlistItem.quote.taxCalculations.bikTaxAverage;
    },

    getMonthlyVehiclePayment(shortlistItem) {
      const rentalsPayment = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_RentalsPayments);
      const maintenanceCost = Number(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_MaintenanceCost);
      return Number((rentalsPayment + maintenanceCost) / shortlistItem.term).toFixed(2);
    },

    getTotalCosts(shortListItem, type) {
      let total = 0;
      let monthlyPayment = 0;
      let businessFuelCost = 0;
      let privateFuelCost = 0;
      let vehicleInsurance = 0;
      if (!shortListItem.quote.taxCalculations) {
        return false;
      }
      switch (type) {
        case 'bch':
          monthlyPayment = Number(this.getBikValue(shortListItem));
          businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.businessMileage_BusinessFuelCost) / shortListItem.term);
          privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostCompanyCar.privateMileage_PrivateFuelCost) / shortListItem.term);
          total = monthlyPayment + businessFuelCost + privateFuelCost;
          break;
        case 'pch':
          monthlyPayment = Number(this.getMonthlyVehiclePayment(shortListItem));
          businessFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.businessMileage_BusinessFuelCost) / shortListItem.term);
          privateFuelCost = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.privateMileage_PrivateFuelCost) / shortListItem.term);
          vehicleInsurance = (Number(shortListItem.quote.taxCalculations.employeeCostPrivateCar.motoringAndFinance_InsuranceCost) / shortListItem.term);
          total = monthlyPayment + businessFuelCost + privateFuelCost + vehicleInsurance;
          break;
      }
      return Number(total).toFixed(2);
    },

    getCashAllowanceNet(shortlistItem) {
      const cashAllowance = Number(this.getMonthlyCost(Math.abs(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_CashAllowance']), shortlistItem));
      const cashIncomeTax = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_IncomeTaxOnCashAlternative'], shortlistItem));
      const cash1NIC = Number(this.getMonthlyCost(shortlistItem.quote.taxCalculations['employeeCostPrivateCar']['cashAllowanceReconciliation_Class1NICOnCashAllowance'], shortlistItem));
      return Number(cashAllowance - (cashIncomeTax + cash1NIC)).toFixed(2);
    },

    getTotalNetCost(shortlistItem, type) {
      switch (type) {
        case 'bch':
          return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostCompanyCar.totals_NetPresentValueNPV, shortlistItem);
        case 'pch':
          return this.getMonthlyCost(shortlistItem.quote.taxCalculations.employeeCostPrivateCar.totals_NetPresentValueNPV, shortlistItem);
      }
    },

    getMonthlyCost(value, shortlistItem) {
      return (Number(value) / shortlistItem.term).toFixed(2);
    },

    async getCostsForSalarySacrificeQuote() {
      EventBus.$emit('loading', true);
      EventBus.$emit('loadingTitle', "Fetching vehicle details");

      const quoteId = this.shortlist.quotes[this.currentQuoteGroup][this.activeQuoteInGroup].quoteID;

      try {
        // Moved to it's own endpoint to save the faffing
        const response = await this.$http.get('/api/get-salary-sacrifice-costs/' + quoteId);

        if (response.data.status !== 200) {
          this.$notify({
            group: 'notify',
            type: 'error',
            title: 'Error',
            text: response.data.error.message
          });

          this.costsForSalarySacrificeQuote = 'NOT_FOUND';

        } else {
          this.costsForSalarySacrificeQuote = response.data.data;
        }


      } catch (err) {
        let response = err.response.data;

        this.$notify({
          group: 'notify',
          type: 'error',
          title: 'Error',
          text: response.message
        });

        this.costsForSalarySacrificeQuote = 'NOT_FOUND';
      }

      EventBus.$emit('loading', false);
    },
    milesPerCalendarMonth() {
      return this.annualMileage / 12;
    },
  },

  computed: {
    hasQuotes() {
      return this.options.length > 0 && this.options[0].quotes.length > 0;
    },

    batterySizeKwh() {
      return this.currentVisibleQuote ? Vehicle.getBatterySizeFromDerivative(this.currentVisibleQuote.derivative) : null
    },

    wLTPRangeMiles() {
      return this.currentVisibleQuote ? Vehicle.getBatteryRangeFromDerivative(this.currentVisibleQuote.derivative) : null;
    },

    currentVisibleQuote() {
      if (this.currentQuoteGroup === null || this.shortlist.quotes.length === 0) {
        return null;
      }

      const currentVisibleQuote = this.shortlist.quotes[this.currentQuoteGroup][this.activeQuoteInGroup];
      if (this.shortlist.name == 'salsac') {
        this.getCostsForSalarySacrificeQuote();
      }

      return currentVisibleQuote;
    },

    currentMpg() {
      return this.currentVisibleQuote.quote.taxCalculations.mpg;
    },

    milesPerKWh() {
      return Vehicle.milesPerKWh(this.wLTPRangeMiles, this.batterySizeKwh);
    },

    averagePencePerMile() {
      return Vehicle.averagePencePerMile(this.totalMonthlyCost, this.milesPerCalendarMonth());
    },

    totalEstimatedElectricCostPerMonth() {
      const milesPerCalendarMonth = this.milesPerCalendarMonth();
      const ppm = this.totalMonthlyCost / milesPerCalendarMonth;

      return Number((ppm * milesPerCalendarMonth) / 100).toFixed(2);
    },

    totalMonthlyCost() {
      const milesPerCalendarMonth = this.milesPerCalendarMonth();
      const milesPerKWh = this.wLTPRangeMiles / this.batterySizeKwh

      const homeChargingStandardMonthlyMiles = this.homeChargingStandardPercentage * milesPerCalendarMonth;
      const publicChargingStandardMonthlyMiles = this.publicChargingStandardPercentage * milesPerCalendarMonth
      const publicChargingFastMonthlyMiles = this.publicChargingFastPercentage * milesPerCalendarMonth;
      const publicChargingFreeMonthlyMiles = this.publicChargingFreePercentage * milesPerCalendarMonth;

      const homeChargingStandardTotalKWhForMonthlyMiles = homeChargingStandardMonthlyMiles / milesPerKWh;
      const publicChargingStandardTotalKWhForMonthlyMiles = publicChargingStandardMonthlyMiles / milesPerKWh;
      const publicChargingFastTotalKWhForMonthlyMiles = publicChargingFastMonthlyMiles / milesPerKWh;
      const publicChargingFreeTotalKWhForMonthlyMiles = publicChargingFreeMonthlyMiles / milesPerKWh;
      const homeChargingStandardMonthlyCost = (+this.adminPortalData.content.global.ev_charging_home / 100) * homeChargingStandardTotalKWhForMonthlyMiles;
      const publicChargingStandardMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_standard / 100) * publicChargingStandardTotalKWhForMonthlyMiles;
      const publicChargingFastMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_fast / 100) * publicChargingFastTotalKWhForMonthlyMiles;
      const publicChargingFreeMonthlyCost = (+this.adminPortalData.content.global.ev_charging_public_free / 100) * publicChargingFreeTotalKWhForMonthlyMiles;
      const totalMonthlyCost = homeChargingStandardMonthlyCost + publicChargingStandardMonthlyCost + publicChargingFastMonthlyCost + publicChargingFreeMonthlyCost;

      return Number(totalMonthlyCost).toFixed(2);
    },

    quoteExpired() {
      return new Date(this.currentVisibleQuote?.quoteExpiryDate) < new Date();
    },
  },

  watch: {
    shortlist(value) {
      if (value) {
        this.selectedShortlist = this.shortlist.shortListID;
      }
    },
    defaultMpg() {
      this.currentVisibleQuote.quote.taxCalculations.mpg = this.resetMPG();
    },

  }
}

</script>
